import { Controller } from "@hotwired/stimulus"
import Drawflow from "drawflow"
import TomSelect from "tom-select"

// Drawflow-backed workflow canvas. Loads the saved graph, lets an admin add /
// connect / configure step nodes, serializes back to the GraphImporter payload,
// and POSTs it. Execution stays linear; the server validates the chain on
// activate. Node config lives in each node's Drawflow `data` and is edited via
// the side panel.
//
// NOTE: the interactive canvas behavior must be verified in a browser — drag,
// connect, configure, save, activate, and the error round-trip. It is also
// desktop-first (a drag canvas isn't usable on a small touch screen); the
// mobile fallback is the read-only run/list views.
export default class extends Controller {
  static targets = ["canvas", "panel", "errors", "name", "triggerForm", "triggerState"]
  static values = {
    graph: Object,        // { name, trigger: {form_id,on_state}, nodes:[...], edges:[...] }
    operations: Array,    // [{ key, name, params:[{key,label,type,required}] }]
    templates: Array,     // [{ id, name, fields:[{id, question}] }]
    contextValues: Array, // [{ key, label }] — run-context values (no form question)
    admins: Array,        // [{ id, email }] — selectable specific notify recipients
    saveUrl: String
  }

  connect() {
    this.editor = new Drawflow(this.canvasTarget)
    this.editor.reorder_animation = false
    this.editor.start()
    this.keyToNodeId = {}
    this.loadGraph()
    this.editor.on("nodeSelected", (id) => this.openPanel(id))
  }

  // ---- Load -------------------------------------------------------------

  loadGraph() {
    const graph = this.graphValue || {}
    ;(graph.nodes || []).forEach((node) => this.addStepNode(node))
    ;(graph.edges || []).forEach((edge) => {
      const from = this.keyToNodeId[edge.from]
      const to = this.keyToNodeId[edge.to]
      if (from && to) this.editor.addConnection(from, to, "output_1", "input_1")
    })
  }

  // ---- Palette (wired to buttons in the view) ---------------------------

  addNotify() { this.addStepNode({ step_key: this.newKey(), step_type: "notify", config: {}, canvas_x: 60, canvas_y: 60 }) }
  addErpPost() { this.addStepNode({ step_key: this.newKey(), step_type: "erp_post", config: {}, erp_mappings: [], canvas_x: 320, canvas_y: 60 }) }
  addWait() { this.addStepNode({ step_key: this.newKey(), step_type: "wait", config: {}, canvas_x: 190, canvas_y: 200 }) }

  static NODE_LABELS = { erp_post: "ERP Post", notify: "Notify", wait: "Wait (gate)" }

  addStepNode(node) {
    const data = {
      step_key: node.step_key || this.newKey(),
      step_type: node.step_type,
      config: node.config || {},
      erp_mappings: node.erp_mappings || []
    }
    const label = this.constructor.NODE_LABELS[node.step_type] || node.step_type
    const html = `<div class="wf-node wf-node-${node.step_type}"><strong>${label}</strong><div class="wf-node-sub" data-key="${data.step_key}"></div></div>`
    const id = this.editor.addNode(
      data.step_type, 1, 1, node.canvas_x || 60, node.canvas_y || 60,
      `wf-${data.step_type}`, data, html
    )
    this.keyToNodeId[data.step_key] = id
    return id
  }

  // ---- Side panel (per-node config) -------------------------------------

  openPanel(nodeId) {
    const node = this.editor.getNodeFromId(nodeId)
    if (!node) return
    this.selectedNodeId = nodeId
    const d = node.data
    let html
    if (d.step_type === "erp_post") html = this.erpPanelHtml(d)
    else if (d.step_type === "wait") html = this.waitPanelHtml(d)
    else html = this.notifyPanelHtml(d)
    this.teardownPanelWidgets()
    this.panelTarget.innerHTML = html
    this.enhancePanelWidgets()
  }

  // Upgrade the specific-admins <select multiple> into a Tom Select tag/search
  // dropdown (selected admins show as removable chips; the rest stay searchable).
  // Rebuilt on each panel render; the underlying <select> stays DOM-synced so
  // applyPanel still reads its selectedOptions.
  enhancePanelWidgets() {
    const sel = this.panelTarget.querySelector('[data-wf="recipient_user_ids"]')
    if (!sel) return

    this.adminSelect = new TomSelect(sel, {
      plugins: ["remove_button"],
      maxItems: null,
      hideSelected: true,
      placeholder: "Search admins…"
    })
  }

  teardownPanelWidgets() {
    if (this.adminSelect) {
      this.adminSelect.destroy()
      this.adminSelect = null
    }
  }

  notifyPanelHtml(d) {
    const c = d.config || {}
    const fields = this.triggerFields()
    const fieldOpts = fields.map(
      (f) => `<option value="${f.id}" ${String(c.recipient_field_id) === String(f.id) ? "selected" : ""}>${this.esc(f.question)}</option>`
    ).join("")
    const chosen = (c.recipient_user_ids || []).map(String)
    const adminOpts = (this.adminsValue || []).map(
      (a) => `<option value="${a.id}" ${chosen.includes(String(a.id)) ? "selected" : ""}>${this.esc(a.email)}</option>`
    ).join("")
    return `
      <h3>Notify</h3>
      <label>Recipients
        <select data-wf="recipients" data-action="change->workflow-builder#applyPanel">
          <option value="org_users" ${c.recipients === "org_users" ? "selected" : ""}>Org members</option>
          <option value="admins" ${c.recipients === "admins" ? "selected" : ""}>All admins</option>
          <option value="specific_admins" ${c.recipients === "specific_admins" ? "selected" : ""}>Specific admin(s)</option>
          <option value="form_field" ${c.recipients === "form_field" ? "selected" : ""}>The vendor named in a form field</option>
        </select></label>
      <label ${c.recipients === "specific_admins" ? "" : "style=\"display:none\""} data-wf-admins>Admin(s) — one email to all selected
        <select data-wf="recipient_user_ids" multiple size="${Math.min(Math.max((this.adminsValue || []).length, 3), 8)}">${adminOpts}</select></label>
      <label ${c.recipients === "form_field" ? "" : "style=\"display:none\""} data-wf-recipient-field>Vendor field
        <select data-wf="recipient_field_id" ${fields.length === 0 ? "disabled" : ""}>
          <option value="">${fields.length === 0 ? "— pick a Trigger form first —" : "— pick a field —"}</option>${fieldOpts}
        </select></label>
      <label>Subject <input type="text" data-wf="subject" value="${this.esc(c.subject)}"></label>
      <label>Body <textarea data-wf="body">${this.esc(c.body)}</textarea></label>
      <button type="button" data-action="workflow-builder#applyPanel">Apply</button>`
  }

  waitPanelHtml(d) {
    const c = d.config || {}
    const reveal = (c.reveal_field_ids || []).map(String)
    const fields = this.triggerFields()
    const checks = fields.length === 0
      ? `<em>Pick a Trigger form to choose which questions this gate unlocks.</em>`
      : fields.map(
          (f) => `<label class="wf-check"><input type="checkbox" data-wfreveal value="${f.id}" ${reveal.includes(String(f.id)) ? "checked" : ""}> ${this.esc(f.question)}</label>`
        ).join("")
    return `
      <h3>Wait — human checkpoint</h3>
      <p class="text-muted">The run pauses here until an admin clears it from the “Action needed” inbox. Clearing it unlocks the questions below on the form.</p>
      <label>Button prompt <input type="text" data-wf="prompt" value="${this.esc(c.prompt)}" placeholder="e.g. Load picked up?"></label>
      <fieldset class="wf-reveal"><legend>Questions this gate unlocks</legend>${checks}</fieldset>
      <button type="button" data-action="workflow-builder#applyPanel">Apply</button>`
  }

  erpPanelHtml(d) {
    const c = d.config || {}
    const ops = this.operationsValue.map(
      (o) => `<option value="${o.key}" ${c.erp_operation === o.key ? "selected" : ""}>${o.name}</option>`
    ).join("")
    const op = this.operationsValue.find((o) => o.key === c.erp_operation)
    const fields = this.triggerFields()
    const rows = op ? op.params.map((p) => this.mappingRowHtml(p, d, fields)).join("") : "<em>Pick an operation to map its fields.</em>"
    return `
      <h3>ERP Post</h3>
      <label>Operation
        <select data-wf="erp_operation" data-action="change->workflow-builder#applyPanel">
          <option value="">— select —</option>${ops}
        </select></label>
      <div class="wf-mappings">${rows}</div>
      <button type="button" data-action="workflow-builder#applyPanel">Apply</button>`
  }

  mappingRowHtml(param, d, fields) {
    const m = (d.erp_mappings || []).find((x) => x.erp_param_key === param.key) || {}
    const kind = m.source_type || "form_field"
    const isConst = kind === "constant"
    const isContext = kind === "context"
    const isStepOut = kind === "step_output"

    // The three sources are mutually exclusive — only the chosen kind's control is
    // enabled. Field options come from the selected TRIGGER form (disabled with a
    // hint when no trigger form is picked); context options are run-context values
    // (vendor that started / was sent the form) needing no form question.
    let fieldControl
    if (fields.length === 0) {
      fieldControl = `<select data-mapfield disabled><option value="">— pick a Trigger form first —</option></select>`
    } else {
      const opts = fields.map(
        (f) => `<option value="${f.id}" ${String(m.source_form_field_id) === String(f.id) ? "selected" : ""}>${this.esc(f.question)}</option>`
      ).join("")
      fieldControl = `<select data-mapfield ${kind === "form_field" ? "" : "disabled"}><option value="">— pick a field —</option>${opts}</select>`
    }

    const contextOpts = (this.contextValuesValue || []).map(
      (c) => `<option value="${c.key}" ${m.context_key === c.key ? "selected" : ""}>${this.esc(c.label)}</option>`
    ).join("")
    const contextControl = `<select data-mapcontext ${isContext ? "" : "disabled"}><option value="">— pick a context value —</option>${contextOpts}</select>`

    // step_output: read a value an EARLIER step recorded (e.g. the document no a
    // create-PO step produced). Source step is any other node; output key is the
    // result key to read (erp_reference for an ERP create).
    const stepOpts = this.stepNodeOptions(d.step_key).map(
      (s) => `<option value="${s.key}" ${m.source_step_key === s.key ? "selected" : ""}>${this.esc(s.label)}</option>`
    ).join("")
    const stepControl = `<select data-mapstep ${isStepOut ? "" : "disabled"}><option value="">— pick a step —</option>${stepOpts}</select>`
    const outputControl = `<input type="text" data-mapoutput placeholder="output key (e.g. erp_reference)" value="${this.esc(m.output_key)}" ${isStepOut ? "" : "disabled"}>`

    return `
      <fieldset class="wf-map" data-param="${param.key}">
        <legend>${this.esc(param.label)}${param.required ? " *" : ""}</legend>
        <select data-mapkind data-action="change->workflow-builder#applyPanel">
          <option value="form_field" ${kind === "form_field" ? "selected" : ""}>Form field</option>
          <option value="constant" ${isConst ? "selected" : ""}>Constant</option>
          <option value="context" ${isContext ? "selected" : ""}>Run context</option>
          <option value="step_output" ${isStepOut ? "selected" : ""}>Earlier step output</option>
        </select>
        ${fieldControl}
        <input type="text" data-mapconst placeholder="constant value" value="${this.esc(m.constant_value)}" ${isConst ? "" : "disabled"}>
        ${contextControl}
        ${stepControl}
        ${outputControl}
      </fieldset>`
  }

  // Other step nodes on the canvas (for step_output source pickers), labelled by
  // type + a short key suffix so duplicates are distinguishable.
  stepNodeOptions(excludeKey) {
    let exported
    try { exported = this.editor.export().drawflow.Home.data } catch { return [] }
    return Object.values(exported)
      .map((n) => ({ key: n.data.step_key, type: n.data.step_type }))
      .filter((s) => s.key && s.key !== excludeKey)
      .map((s) => ({ key: s.key, label: `${this.constructor.NODE_LABELS[s.type] || s.type} [${String(s.key).slice(-4)}]` }))
  }

  // Re-render the open node panel — used when the Trigger form changes, so an
  // open ERP step picks up that form's fields for its mapping dropdowns.
  refreshPanel() {
    if (this.selectedNodeId != null) this.openPanel(this.selectedNodeId)
  }

  applyPanel() {
    const node = this.editor.getNodeFromId(this.selectedNodeId)
    if (!node) return
    const data = { ...node.data }
    const get = (sel) => this.panelTarget.querySelector(`[data-wf="${sel}"]`)

    if (data.step_type === "notify") {
      const adminSel = this.panelTarget.querySelector('[data-wf="recipient_user_ids"]')
      data.config = {
        recipients: get("recipients")?.value,
        recipient_field_id: get("recipient_field_id")?.value || null,
        recipient_user_ids: adminSel ? Array.from(adminSel.selectedOptions).map((o) => Number(o.value)) : [],
        subject: get("subject")?.value,
        body: get("body")?.value
      }
    } else if (data.step_type === "wait") {
      data.config = {
        prompt: get("prompt")?.value,
        reveal_field_ids: Array.from(this.panelTarget.querySelectorAll("[data-wfreveal]:checked")).map((cb) => Number(cb.value))
      }
    } else {
      data.config = { erp_operation: get("erp_operation")?.value }
      data.erp_mappings = Array.from(this.panelTarget.querySelectorAll(".wf-map")).map((fs) => {
        const kind = fs.querySelector("[data-mapkind]").value
        return {
          erp_param_key: fs.dataset.param,
          source_type: kind,
          source_form_field_id: kind === "form_field" ? fs.querySelector("[data-mapfield]").value : null,
          constant_value: kind === "constant" ? fs.querySelector("[data-mapconst]").value : null,
          context_key: kind === "context" ? fs.querySelector("[data-mapcontext]").value : null,
          source_step_key: kind === "step_output" ? fs.querySelector("[data-mapstep]").value : null,
          output_key: kind === "step_output" ? fs.querySelector("[data-mapoutput]").value : null
        }
      })
    }
    this.editor.updateNodeDataFromId(this.selectedNodeId, data)
    this.openPanel(this.selectedNodeId) // re-render (operation change refreshes rows)
  }

  // ---- Serialize + save -------------------------------------------------

  save(event) { this.persist(event, false) }
  activate(event) { this.persist(event, true) }

  async persist(event, active) {
    event?.preventDefault()
    const payload = { graph: { ...this.serialize(), active } }
    const res = await fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      body: JSON.stringify(payload)
    })
    const json = await res.json().catch(() => ({}))
    if (res.ok && json.ok) {
      window.location = json.redirect
    } else {
      this.errorsTarget.textContent = (json.errors || ["Save failed."]).join(" • ")
    }
  }

  serialize() {
    const exported = this.editor.export().drawflow.Home.data
    const idToKey = {}
    const nodes = Object.values(exported).map((n) => {
      idToKey[n.id] = n.data.step_key
      return {
        step_key: n.data.step_key,
        step_type: n.data.step_type,
        canvas_x: Math.round(n.pos_x),
        canvas_y: Math.round(n.pos_y),
        config: n.data.config || {},
        erp_mappings: n.data.erp_mappings || []
      }
    })
    const edges = []
    Object.values(exported).forEach((n) => {
      const outs = n.outputs?.output_1?.connections || []
      outs.forEach((c) => edges.push({ from: n.data.step_key, to: idToKey[c.node] }))
    })
    return {
      name: this.hasNameTarget ? this.nameTarget.value : this.graphValue.name,
      trigger: {
        form_id: this.hasTriggerFormTarget ? this.triggerFormTarget.value : null,
        on_state: this.hasTriggerStateTarget ? this.triggerStateTarget.value : "submitted"
      },
      nodes,
      edges
    }
  }

  // ---- helpers ----------------------------------------------------------

  triggerFields() {
    const id = this.hasTriggerFormTarget ? this.triggerFormTarget.value : null
    const tpl = (this.templatesValue || []).find((t) => String(t.id) === String(id))
    return tpl ? tpl.fields : []
  }

  newKey() { return "n_" + Math.random().toString(36).slice(2, 10) }
  esc(v) { return (v == null ? "" : String(v)).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])) }
  csrfToken() { return document.querySelector('meta[name="csrf-token"]')?.content }
}
